import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ColDef, GridReadyEvent, RowValueChangedEvent, SideBarDef } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { Investment, InvestmentSummaryInvestment } from '../../../types/investment/Investment'
import InvestmentModal from './InvestmentModal'
import { useDeleteInvestment } from '../hooks/useDeleteInvestment'
import { useUpdateInvestment } from '../hooks/useUpdateInvestment'
import InvestmentDeprecationModal from '../../investmentDepreciation/components/InvestmentDeprecationModal'
import { formatAsDate } from '../../../config/dayjsConfig'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import './InvestmentGrid.css'
import { useInvestmentColumns } from '../hooks/useInvestmentColumns'
import { DeprecationMethodId } from '../../../types/deprecation/DeprecationMethod'
import { getStatusBarConfig } from '../../../shared/utils/agGridConfig'
import { showWarningMessage } from '../../../utils/message'
import { filtersSelector } from '../../../redux/context/filters/selectors'

interface InvestmentGridProps {
  investments: InvestmentSummaryInvestment[]
  isExisting: boolean
}

const TAX_DEPRECIATION_COLUMNS = [
  'taxDeprecationPlan.deprecationMethodId',
  'taxDeprecationPlan.startDate',
  'taxDeprecationPlan.value',
  'taxDeprecationPlan.plAccountCode',
  'taxDeprecationPlan.bsAccountCode',
  'summary.taxDeprecation',
  'summary.taxFiscalYearDeprecation',
  'summary.taxResidual'
]

const sideBar: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true
      }
    }
  ],
  defaultToolPanel: ''
}

const TAX_DEPRECATION_REQUIRED_FIELDS = ['startDate', 'deprecationMethodId', 'value', 'plAccountCode', 'bsAccountCode']

const isTaxDeprecationPartiallyFilled = (plan: Record<string, unknown> | undefined) => {
  if (!plan || JSON.stringify(plan) === '{}') return false
  const filled = TAX_DEPRECATION_REQUIRED_FIELDS.filter(f => plan[f] != null)
  return filled.length > 0 && filled.length < TAX_DEPRECATION_REQUIRED_FIELDS.length
}

const InvestmentGrid: React.FC<InvestmentGridProps> = ({ investments, isExisting }) => {
  const { t } = useTranslation()
  const { budgetingScenario } = useSelector(filtersSelector)
  const { mutate: deleteInvestment } = useDeleteInvestment(isExisting)
  const updateInvestment = useUpdateInvestment(isExisting)
  const [investment, setInvestment] = useState<InvestmentSummaryInvestment>()
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [depreciationModalVisible, setDepreciationModalVisible] = useState<boolean>(false)
  const gridRef = useRef<AgGridReact>(null)
  const editingRowIdRef = useRef<number | null>(null)
  const originalRowDataRef = useRef<InvestmentSummaryInvestment | null>(null)

  const investmentData = useMemo(
    () =>
      investments
        .slice()
        .sort((a, b) => a.id - b.id)
        .map(inv => ({
          ...inv,
          deprecationPlan: {
            ...inv.deprecationPlan,
            value:
              inv.deprecationPlan?.deprecationMethodId === DeprecationMethodId.DecliningBalance
                ? Math.round(inv.deprecationPlan.value * 1000000) / 10000
                : Math.round(inv.deprecationPlan?.value)
          },
          ...(inv.taxDeprecationPlan && {
            taxDeprecationPlan: {
              ...inv.taxDeprecationPlan,
              value:
                inv.taxDeprecationPlan?.deprecationMethodId === DeprecationMethodId.DecliningBalance
                  ? Math.round(inv.taxDeprecationPlan.value * 1000000) / 10000
                  : Math.round(inv.taxDeprecationPlan?.value)
            }
          })
        })),
    [investments]
  )

  const toggleModal = () => {
    setModalVisible(!modalVisible)
  }

  const onDepreciationClick = useCallback((inv: InvestmentSummaryInvestment) => {
    setInvestment(inv)
    setDepreciationModalVisible(true)
  }, [])

  const onEditClick = useCallback((inv: InvestmentSummaryInvestment) => {
    setInvestment(inv)
    setModalVisible(true)
  }, [])

  const onDelete = useCallback(
    (inv: InvestmentSummaryInvestment) => {
      deleteInvestment(inv)
    },
    [deleteInvestment]
  )

  const onStartEdit = useCallback((inv: InvestmentSummaryInvestment) => {
    editingRowIdRef.current = inv.id
    const rowNode = gridRef.current?.api.getRowNode(String(inv.id))
    originalRowDataRef.current = rowNode?.data ? structuredClone(rowNode.data) : null
    if (rowNode?.rowIndex != null) {
      gridRef.current?.api.setFocusedCell(rowNode.rowIndex, 'name')
      gridRef.current?.api.startEditingCell({
        rowIndex: rowNode.rowIndex,
        colKey: 'name'
      })
    }
    setTimeout(() => {
      gridRef.current?.api?.refreshCells({ columns: ['actions'], force: true })
    }, 0)
  }, [])

  const onSaveEdit = useCallback(() => {
    gridRef.current?.api.stopEditing(false)
  }, [])

  const onCancelEdit = useCallback(() => {
    const id = editingRowIdRef.current
    gridRef.current?.api.stopEditing(true)
    if (originalRowDataRef.current !== null && id !== null) {
      const rowNode = gridRef.current?.api.getRowNode(String(id))
      rowNode?.setData(originalRowDataRef.current)
    }
    originalRowDataRef.current = null
  }, [])

  const { columnDefs } = useInvestmentColumns({
    investments,
    isExisting,
    isLocked: budgetingScenario?.isLocked === true,
    onDepreciationClick,
    onEditClick,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete
  })

  const gridContext = useMemo(() => ({ editingRowIdRef }), [])

  const toApiValue = (value: number | undefined, methodId: number | undefined) => {
    if (value == null) return value
    return methodId === 2 ? value / 100 : value
  }

  const onRowValueChanged = useCallback(
    (event: RowValueChangedEvent<InvestmentSummaryInvestment>) => {
      if (!event.data) return

      const { summary: _summary, ...saveData } = event.data
      let { taxDeprecationPlan } = saveData

      // Validate partial tax deprecation plan fill
      if (isTaxDeprecationPartiallyFilled(taxDeprecationPlan as Record<string, unknown> | undefined)) {
        showWarningMessage(t('investmentsPage:taxDeprecationPartialFill'))
        gridRef.current?.api.refreshCells({ force: true })
        return
      }

      // Check if the tax depreciation plan data is not empty
      if (taxDeprecationPlan && JSON.stringify(taxDeprecationPlan) !== '{}' && taxDeprecationPlan.startDate) {
        taxDeprecationPlan = {
          ...taxDeprecationPlan,
          startDate: formatAsDate(taxDeprecationPlan.startDate) || '',
          value: toApiValue(taxDeprecationPlan.value, taxDeprecationPlan.deprecationMethodId) ?? 0
        }
      } else {
        delete saveData.taxDeprecationPlan
      }

      const formattedValues = {
        ...saveData,
        date: formatAsDate(saveData.date) || '',
        deprecationPlan: {
          ...saveData.deprecationPlan,
          startDate: formatAsDate(saveData.deprecationPlan.startDate) || '',
          value: toApiValue(saveData.deprecationPlan.value, saveData.deprecationPlan.deprecationMethodId) ?? 0
        },
        ...(taxDeprecationPlan &&
          JSON.stringify(taxDeprecationPlan) !== '{}' &&
          taxDeprecationPlan.startDate && {
            taxDeprecationPlan
          })
      }

      updateInvestment.mutate(formattedValues)
    },
    [updateInvestment]
  )

  const onRowEditingStopped = useCallback(() => {
    editingRowIdRef.current = null
    gridRef.current?.api?.refreshCells({ columns: ['actions'], force: true })
  }, [])

  const onGridReady = useCallback((event: GridReadyEvent) => {
    event.api.setColumnsVisible(TAX_DEPRECIATION_COLUMNS, false)
  }, [])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 60,
      suppressHeaderMenuButton: true,
      cellDataType: false,
      suppressKeyboardEvent: params => {
        if (params.editing) return params.event.key === 'Enter' || params.event.key === 'Escape'
        const { key } = params.event
        return key === 'Enter' || key === 'F2' || key === 'Backspace' || key === 'Delete' || key.length === 1
      }
    }),
    []
  )

  return (
    <>
      {investment && (
        <InvestmentDeprecationModal
          investmentId={investment.id}
          visible={depreciationModalVisible}
          handleOk={() => setDepreciationModalVisible(false)}
        />
      )}
      {modalVisible && investment && (
        <InvestmentModal
          onlyDepreciable
          visible={modalVisible}
          sourceDisabled={false}
          isExisting={isExisting}
          id={investment.id}
          onFinish={toggleModal}
        />
      )}
      <div className={`investment-grid ${investmentData.length > 20 ? 'investment-grid--fixed' : ''}`}>
        <CustomAgGrid
          ref={gridRef}
          rowData={investmentData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={gridContext}
          sideBar={sideBar}
          statusBar={getStatusBarConfig()}
          cellSelection
          suppressClipboardPaste
          editType="fullRow"
          suppressClickEdit
          popupParent={document.body}
          onGridReady={onGridReady}
          onRowValueChanged={onRowValueChanged}
          onRowEditingStopped={onRowEditingStopped}
          getRowId={p => String(p.data.id)}
          domLayout={investmentData.length > 20 ? 'normal' : 'autoHeight'}
        />
      </div>
    </>
  )
}

export default InvestmentGrid
